import { useState, useEffect } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Check, CreditCard, AlertCircle, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    price: 49,
    currency: '€',
    visualizations: '400',
    features: [
      'White-label studio website',
      'AI-powered wrap visualizer',
      '400 visualizations/month',
      'Lead capture & management',
      'Email support'
    ]
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 79,
    currency: '€',
    visualizations: '1,000',
    popular: true,
    features: [
      'Everything in Starter',
      '1,000 visualizations/month',
      'Render history',
      'Website import tool',
      'Priority support'
    ]
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 119,
    currency: '€',
    visualizations: 'Unlimited',
    features: [
      'Everything in Growth',
      'Unlimited visualizations',
      'Custom domain support',
      'Advanced analytics',
      'Dedicated support'
    ]
  }
];

const BillingPage = () => {
  const { studio, setStudio } = useOutletContext();
  const { getAuthHeader } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const currentPlan = studio?.subscription_plan || 'none';
  const isActive = studio?.subscription_status === 'active';

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error('Payment status check timed out. Please refresh the page.');
      return;
    }

    setCheckingStatus(true);
    try {
      const response = await axios.get(`${API}/billing/status/${sessionId}`, { headers: getAuthHeader() });
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your studio is now active.');
        const studioRes = await axios.get(`${API}/studios/me`, { headers: getAuthHeader() });
        setStudio(studioRes.data);
        setCheckingStatus(false);
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingStatus(false);
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Status check failed:', error);
      setCheckingStatus(false);
    }
  };

  const getPlanIndex = (planId) => plans.findIndex(p => p.id === planId);

  const handlePlanSelect = (plan) => {
    if (!isActive) {
      // New subscription
      handleSubscribe(plan.id);
    } else if (plan.id !== currentPlan) {
      // Plan change
      setSelectedPlan(plan);
      setShowConfirmModal(true);
    }
  };

  const handleSubscribe = async (planId) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/billing/checkout`, {
        origin_url: window.location.origin,
        plan: planId
      }, { headers: getAuthHeader() });

      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  const handlePlanChange = async () => {
    if (!selectedPlan) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/billing/change-plan`, {
        new_plan: selectedPlan.id
      }, { headers: getAuthHeader() });

      const isUpgrade = getPlanIndex(selectedPlan.id) > getPlanIndex(currentPlan);
      
      if (isUpgrade) {
        toast.success(`Upgraded to ${selectedPlan.name}! Changes are effective immediately.`);
      } else {
        toast.success(`Plan changed to ${selectedPlan.name}. This will take effect at your next billing date.`);
      }
      
      // Refresh studio data
      const studioRes = await axios.get(`${API}/studios/me`, { headers: getAuthHeader() });
      setStudio(studioRes.data);
      setShowConfirmModal(false);
    } catch (error) {
      toast.error('Failed to change plan. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  const isUpgrade = selectedPlan && getPlanIndex(selectedPlan.id) > getPlanIndex(currentPlan);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="billing-page"
    >
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Billing</h1>
        <p className="text-white/60">Manage your subscription</p>
      </div>

      {checkingStatus && (
        <div className="bg-[#FFFFFF]/10 border border-[#FFFFFF]/30 rounded-sm p-4 mb-8 flex items-center gap-3">
          <span className="spinner w-5 h-5" style={{ borderTopColor: '#FFFFFF' }}></span>
          <span>Verifying your payment...</span>
        </div>
      )}

      {/* Current Status */}
      <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">Current Plan</h2>
          <span className={`px-3 py-1 rounded text-sm font-medium ${
            isActive ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
          }`}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        </div>

        {isActive ? (
          <div>
            <p className="text-2xl font-bold text-gold-gradient mb-2">
              {plans.find(p => p.id === currentPlan)?.name || 'Founding Partner'}
            </p>
            <p className="text-white/60">
              {plans.find(p => p.id === currentPlan)?.currency || '€'}
              {plans.find(p => p.id === currentPlan)?.price || 149}/month
            </p>
          </div>
        ) : (
          <div>
            <p className="text-white/60 mb-4">
              Subscribe to publish your studio and enable the visualizer for customers.
            </p>
            <div className="flex items-start gap-3 bg-[#171717] p-3 rounded-sm">
              <AlertCircle className="text-yellow-400 flex-shrink-0 mt-0.5" size={18} />
              <p className="text-sm text-white/70">
                Your studio is in draft mode. Customers cannot access the visualizer until you subscribe.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Plans */}
      <h2 className="text-xl font-bold mb-4">{isActive ? 'Change Plan' : 'Choose a Plan'}</h2>
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {plans.map((plan) => {
          const isCurrent = isActive && plan.id === currentPlan;
          const isPlanUpgrade = isActive && getPlanIndex(plan.id) > getPlanIndex(currentPlan);
          const isPlanDowngrade = isActive && getPlanIndex(plan.id) < getPlanIndex(currentPlan);
          
          return (
            <div
              key={plan.id}
              className={`bg-[#0F0F0F] border rounded-sm p-6 relative ${
                isCurrent ? 'border-green-500' : plan.popular ? 'border-[#FF3399]' : 'border-white/10'
              }`}
            >
              {plan.popular && !isCurrent && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF3399] text-black text-xs font-bold px-3 py-1">
                  MOST POPULAR
                </div>
              )}
              {isCurrent && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-black text-xs font-bold px-3 py-1">
                  CURRENT PLAN
                </div>
              )}

              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="text-[#FF3399]" size={20} />
                <h3 className="font-semibold text-lg">{plan.name}</h3>
              </div>

              <div className="text-3xl font-black text-gold-gradient mb-1">
                {plan.currency}{plan.price}
                <span className="text-sm text-white/50 font-normal">/month</span>
              </div>
              <p className="text-white/50 text-sm mb-4">{plan.visualizations} visualizations</p>

              <ul className="space-y-2 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-white/70 text-sm">
                    <Check size={14} className="text-green-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <div className="text-center text-white/40 text-sm py-2">
                  Your current plan
                </div>
              ) : (
                <Button
                  onClick={() => handlePlanSelect(plan)}
                  disabled={loading}
                  className={`w-full font-bold py-4 ${
                    isPlanUpgrade
                      ? 'bg-[#FF3399] text-white hover:bg-[#FF3399]/90'
                      : isPlanDowngrade
                      ? 'bg-zinc-700 text-white hover:bg-zinc-600'
                      : 'bg-[#FFFFFF] text-black hover:bg-[#E0E0E0]'
                  }`}
                >
                  {loading ? (
                    <span className="spinner w-5 h-5"></span>
                  ) : isPlanUpgrade ? (
                    <span className="flex items-center gap-2"><ArrowUp size={16} /> Upgrade</span>
                  ) : isPlanDowngrade ? (
                    <span className="flex items-center gap-2"><ArrowDown size={16} /> Downgrade</span>
                  ) : (
                    'Subscribe'
                  )}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Confirm Modal */}
      {showConfirmModal && selectedPlan && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0F0F0F] border border-white/10 max-w-md w-full p-6 rounded-sm">
            <h3 className="text-xl font-bold mb-4">
              {isUpgrade ? 'Confirm Upgrade' : 'Confirm Downgrade'}
            </h3>
            
            <div className="mb-6">
              <p className="text-white/70 mb-4">
                You're changing from <span className="text-white font-medium">{plans.find(p => p.id === currentPlan)?.name}</span> to{' '}
                <span className="text-white font-medium">{selectedPlan.name}</span>.
              </p>
              
              {isUpgrade ? (
                <div className="bg-green-500/10 border border-green-500/30 p-3 rounded-sm">
                  <p className="text-green-400 text-sm flex items-center gap-2">
                    <ArrowUp size={16} />
                    <span>Your upgrade will be <strong>activated immediately</strong>. You'll be charged the difference for the remaining billing period.</span>
                  </p>
                </div>
              ) : (
                <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 rounded-sm">
                  <p className="text-yellow-400 text-sm flex items-center gap-2">
                    <ArrowDown size={16} />
                    <span>Your downgrade will take effect at your <strong>next billing date</strong>. You'll keep your current plan until then.</span>
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 bg-zinc-700 hover:bg-zinc-600"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePlanChange}
                className={`flex-1 ${isUpgrade ? 'bg-[#FF3399] hover:bg-[#FF3399]/90' : 'bg-white text-black hover:bg-gray-200'}`}
                disabled={loading}
              >
                {loading ? <span className="spinner w-5 h-5"></span> : 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default BillingPage;
